import { Component, Input } from '@angular/core';
import { HelpCircle, LucideAngularModule } from 'lucide-angular';
import { NgClass } from '@angular/common';
import { RightSidebarComponent } from '@app/partials/right-sidebar/right-sidebar.component';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';

/**
 * TVG-237: which page the help panel is opened from. Drives the "This page"
 * tab content only — the "Full process" tab is identical everywhere.
 */
export type HelpPanelPage = 'board' | 'contact' | 'quote';

type HelpPanelTab = 'page' | 'full';

/**
 * TVG-237: staff-facing "How this works" help panel.
 *
 * Renders a persistent trigger button plus a slide-out drawer (reusing
 * {@link RightSidebarComponent}) with two tabs: a page-specific glossary
 * ("This page") and the shared booking-journey timeline ("Full process").
 *
 * The whole thing is gated behind the `help-panel` feature toggle — when the
 * toggle is off nothing is rendered, so hosts can drop `<app-help-panel>` into
 * their header unconditionally.
 */
@Component({
  selector: 'app-help-panel',
  standalone: true,
  imports: [LucideAngularModule, NgClass, RightSidebarComponent],
  templateUrl: './help-panel.component.html',
  styles: [':host { display: inline-flex; }'],
})
export class HelpPanelComponent {
  /** Which page the panel is opened from; selects the "This page" content. */
  @Input({ required: true }) page!: HelpPanelPage;

  protected isOpen = false;
  protected activeTab: HelpPanelTab = 'page';

  /** Resolved once — toggles are loaded after login and stable thereafter. */
  protected readonly enabled: boolean;

  protected readonly HelpIcon = HelpCircle;

  constructor(private featureToggleService: FeatureToggleService) {
    this.enabled = this.featureToggleService.isEnabled('help-panel');
  }

  protected openPanel(): void {
    this.activeTab = 'page';
    this.isOpen = true;
  }

  protected closePanel(): void {
    this.isOpen = false;
  }

  protected selectTab(tab: HelpPanelTab): void {
    this.activeTab = tab;
  }
}
